import React from 'react'
import { useAuthStore, type Plan } from './useAuthStore'

const FEATURES = [
  { icon: '🌐', name: 'Web App', detail: 'React + Vite · runs in any browser · PWA-ready · core UI for all platforms', plan: 'free' as Plan },
  { icon: '🖥', name: 'Electron Shell', detail: 'Wraps the web app · native file system · desktop menus · auto-updater', plan: 'free' as Plan },
  { icon: '🧩', name: 'VS Code Extension', detail: 'Webview panel · same React app embedded · talks to VS Code API for files', plan: 'free' as Plan },
  { icon: '⚡', name: 'AI Backend', detail: 'Node.js + Express · streams Claude API · manages context windows per session', plan: 'pro' as Plan },
  { icon: '🔴', name: 'Collab Server', detail: 'WebSocket + Socket.io · CRDT sync · presence · shared AI sessions', plan: 'pro' as Plan },
  { icon: '🎙', name: 'Voice Pipeline', detail: 'Whisper STT → LLM → TTS · real-time streaming · wake-word detection', plan: 'pro' as Plan },
]

const VIBE_FEATURES = [
  { icon: '✏️', title: 'CodeMirror 6 Editor', desc: 'Syntax highlighting · multi-cursor · bracket matching · minimap' },
  { icon: '📁', title: 'Smart File Tree', desc: 'Virtual FS · create/rename/delete · drag-to-move · git status' },
  { icon: '🤖', title: 'AI Chat Panel', desc: 'Streaming responses · code blocks · copy/apply · message history' },
  { icon: '👻', title: 'Ghost Text Completions', desc: 'Inline AI suggestions · Tab to accept · context-aware' },
  { icon: '🔀', title: 'Diff View + Apply', desc: 'Side-by-side diff · one-click apply patches the file' },
  { icon: '💊', title: 'Context Pills', desc: 'Auto-detect open files, folders, git branch as AI context' },
  { icon: '🎯', title: 'AI Modes', desc: 'Ask · Edit · Agent — conversational, inline diff, autonomous multi-step' },
  { icon: '🔊', title: 'Voice Commands', desc: '"Hey Pulse" wake-word · STT → LLM → TTS · hands-free coding' },
  { icon: '👥', title: 'Real-Time Collab', desc: 'Live cursors · presence avatars · shared AI sessions · CRDT sync' },
  { icon: '🖥️', title: 'Desktop App', desc: 'Native file system · terminal · auto-updater · offline mode' },
  { icon: '⌨️', title: 'Command Palette', desc: 'Ctrl+K · full hotkey map · rebindable shortcuts' },
  { icon: '🚀', title: 'Instant Preview', desc: 'Live preview panel · hot reload · responsive testing' },
]

const PLANS: { id: Plan; name: string; price: string; period: string; features: string[]; accent: string; badge?: string }[] = [
  {
    id: 'free', name: 'Free', price: '$0', period: '/month', accent: 'var(--text-1)',
    features: [
      'Web IDE in browser',
      'CodeMirror 6 editor',
      'File tree & tabs',
      'Basic AI chat (limited)',
      'VS Code extension',
      'Community support',
    ],
  },
  {
    id: 'pro', name: 'Pro', price: '$20', period: '/month', accent: 'var(--accent)', badge: 'POPULAR',
    features: [
      'Everything in Free',
      'Unlimited AI streaming',
      'Voice pipeline (STT + TTS)',
      'Ghost text completions',
      'AI modes (Ask/Edit/Agent)',
      'Real-time collab (5 users)',
      'Electron desktop app',
      'Priority support',
    ],
  },
  {
    id: 'unlimited', name: 'Unlimited', price: '$49', period: '/month', accent: 'var(--accent-3)',
    features: [
      'Everything in Pro',
      'Unlimited collab users',
      'Shared AI sessions',
      'Custom AI models (Ollama)',
      'Offline mode + local LLM',
      'Team admin dashboard',
      'SSO & advanced auth',
      'Dedicated support',
    ],
  },
]

export function PlansPage({ onContinue }: { onContinue: () => void }) {
  const { user, upgradePlan } = useAuthStore()
  const currentPlan = user?.plan ?? 'free'

  const handleSelect = async (plan: Plan) => {
    await upgradePlan(plan)
    onContinue()
  }

  return (
    <div className="plans-page">
      {/* Hero */}
      <div className="plans-hero">
        <div className="plans-hero__logo">
          <span className="pulse-dot" style={{ width: 10, height: 10 }} />
          <span className="auth-logo-text">PULSE</span>
        </div>
        <h1 className="plans-hero__title">
          The AI IDE that <span style={{ color: 'var(--accent)' }}>beats Cursor</span>
        </h1>
        <p className="plans-hero__sub">
          Voice-native · Real-time collab · Smarter context · Runs everywhere
        </p>
      </div>

      {/* Architecture cards */}
      <div className="plans-section-label">
        <span>Core Architecture</span>
        <span className="goals-section-line" />
      </div>
      <div className="plans-arch-grid">
        {FEATURES.map((f) => (
          <div key={f.name} className="plans-arch-card">
            <div style={{ fontSize: 22 }}>{f.icon}</div>
            <div className="plans-arch-card__name">{f.name}</div>
            <div className="plans-arch-card__detail">{f.detail}</div>
            {f.plan !== 'free' && (
              <span className="plans-arch-card__badge">PRO</span>
            )}
          </div>
        ))}
      </div>

      {/* All vibe coding features */}
      <div className="plans-section-label" style={{ marginTop: 40 }}>
        <span>All Features</span>
        <span className="goals-section-line" />
      </div>
      <div className="plans-features-grid">
        {VIBE_FEATURES.map((f) => (
          <div key={f.title} className="plans-feature-card">
            <span style={{ fontSize: 18 }}>{f.icon}</span>
            <div>
              <div className="plans-feature-card__title">{f.title}</div>
              <div className="plans-feature-card__desc">{f.desc}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Pricing */}
      <div className="plans-section-label" style={{ marginTop: 48 }}>
        <span>Choose Your Plan</span>
        <span className="goals-section-line" />
      </div>
      <div className="plans-grid">
        {PLANS.map((p) => (
          <div
            key={p.id}
            className={`plans-card ${p.id === 'pro' ? 'plans-card--featured' : ''}`}
            style={{ '--plan-accent': p.accent } as React.CSSProperties}
          >
            {p.badge && <div className="plans-card__badge">{p.badge}</div>}
            <div className="plans-card__name">{p.name}</div>
            <div className="plans-card__price">
              {p.price}<span className="plans-card__period">{p.period}</span>
            </div>
            <ul className="plans-card__features">
              {p.features.map((f) => (
                <li key={f}>
                  <span style={{ color: 'var(--accent-2)' }}>✓</span> {f}
                </li>
              ))}
            </ul>
            <button
              className={`plans-card__btn ${currentPlan === p.id ? 'plans-card__btn--current' : ''}`}
              onClick={() => handleSelect(p.id)}
            >
              {currentPlan === p.id ? 'Current Plan' : p.id === 'free' ? 'Get Started' : 'Upgrade'}
            </button>
          </div>
        ))}
      </div>

      {/* Skip */}
      <div className="plans-skip">
        <button onClick={onContinue}>Skip for now →</button>
      </div>
    </div>
  )
}
